import {
    buildDebtBalancesConfirmedMessage,
    type DebtBalanceEntry,
} from '@fruitful/llm-service-shared';
import type { DebtBalanceSlidersWidgetProps } from '@fruitful/types';
import React, { useCallback, useState } from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    H6Regular,
    Input,
    LabelEmphasized,
    SlideRulePicker,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { CHAT_INPUT_WIDGET } from '../shared-chat/ChatLayout';
import { BaseInputWidget } from './BaseInputWidget';

interface DebtBalanceSlidersWidgetComponentProps extends DebtBalanceSlidersWidgetProps {
    disabled?: boolean;
    isActive?: boolean;
    onSubmit: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
}

const MIN = 0;
const MAX = 100000;
const STEP = 100;
const INITIAL_VALUE = 0;

const formatCurrency = (value: number): string =>
    `$${Math.round(value).toLocaleString()}`;

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

export const DebtBalanceSlidersWidget = ({
    entries,
    isJoint,
    disabled = false,
    isActive = true,
    onSubmit,
}: DebtBalanceSlidersWidgetComponentProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [balances, setBalances] = useState<number[]>(() =>
        entries.map(() => INITIAL_VALUE),
    );
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isManualMode, setIsManualMode] = useState(false);
    const [manualText, setManualText] = useState('');

    const isWidgetDisabled = disabled || !isActive || hasSubmitted;
    const entry = entries[currentIndex];
    const isLastStep = currentIndex === entries.length - 1;

    const subject = isJoint ? 'do you and your partner' : 'do you';
    const promptText =
        entry != null
            ? `About how much ${subject} owe across all ${entry.label.toLowerCase()}?`
            : '';

    const handleValueChange = useCallback(
        (next: number) => {
            setBalances((prev) => {
                const updated = [...prev];
                updated[currentIndex] = next;
                return updated;
            });
        },
        [currentIndex],
    );

    const submitAll = useCallback(() => {
        if (isWidgetDisabled) return;

        const debtBalances: DebtBalanceEntry[] = entries.map((e, i) => ({
            type: e.type,
            label: e.label,
            value: balances[i] ?? 0,
        }));

        const displayParts = debtBalances.map(
            (b) => `${b.label}: ${formatCurrency(b.value)}`,
        );
        const displayText = displayParts.join('\n');

        const agentPayload = buildDebtBalancesConfirmedMessage({
            displayText,
            payload: { balances: debtBalances },
        });

        setHasSubmitted(true);
        void onSubmit(displayText, agentPayload);
    }, [isWidgetDisabled, entries, balances, onSubmit]);

    const handleNext = useCallback(() => {
        if (isWidgetDisabled) return;

        if (isLastStep) {
            submitAll();
        } else {
            setCurrentIndex((prev) => prev + 1);
            setIsManualMode(false);
        }
    }, [isWidgetDisabled, isLastStep, submitAll]);

    const openManualEntry = useCallback(() => {
        if (isWidgetDisabled) return;
        setManualText(String(Math.round(balances[currentIndex] ?? 0)));
        setIsManualMode(true);
    }, [isWidgetDisabled, balances, currentIndex]);

    const cancelManualEntry = useCallback(() => {
        setIsManualMode(false);
    }, []);

    const submitManualEntry = useCallback(() => {
        if (isWidgetDisabled) return;
        const cleaned = manualText.replace(/[^0-9.]/g, '');
        const parsed = cleaned === '' ? NaN : Number(cleaned);
        const current = balances[currentIndex] ?? INITIAL_VALUE;
        const next = Number.isNaN(parsed) ? current : clamp(parsed, MIN, MAX);
        handleValueChange(next);
        setIsManualMode(false);
    }, [
        isWidgetDisabled,
        manualText,
        balances,
        currentIndex,
        handleValueChange,
    ]);

    if (entry == null) return null;

    return (
        <BaseInputWidget
            alignSelf="stretch"
            width="100%"
            maxWidth="100%"
            paddingTop={CHAT_INPUT_WIDGET.paddingTop}
            paddingBottom={12}
        >
            <YStack
                gap={12}
                paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
            >
                <YStack gap={4}>
                    {entries.length > 1 && (
                        <LabelEmphasized>
                            ({currentIndex + 1} OF {entries.length})
                        </LabelEmphasized>
                    )}
                    <H6Regular paddingBottom={4}>{promptText}</H6Regular>
                </YStack>

                {isManualMode ? (
                    <YStack gap={12}>
                        <Input
                            value={manualText}
                            onChangeText={setManualText}
                            keyboardType="numeric"
                            placeholder="Enter an exact amount"
                            onSubmitEditing={submitManualEntry}
                            returnKeyType="done"
                        />
                        <XStack gap={8}>
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Cancel"
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.LARGE}
                                    onPress={cancelManualEntry}
                                />
                            </XStack>
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Set"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    disabled={isWidgetDisabled}
                                    onPress={submitManualEntry}
                                />
                            </XStack>
                        </XStack>
                    </YStack>
                ) : (
                    <YStack
                        marginHorizontal={
                            -CHAT_INPUT_WIDGET.rowPaddingHorizontal
                        }
                        gap={24}
                    >
                        <SlideRulePicker
                            min={MIN}
                            max={MAX}
                            step={STEP}
                            initialValue={
                                balances[currentIndex] ?? INITIAL_VALUE
                            }
                            verticalGap={10}
                            shortStepColor="$gray900"
                            longStepColor="$gray900"
                            textFormatter={(raw: string) =>
                                formatCurrency(Number(raw))
                            }
                            onValueChangeEnd={handleValueChange}
                            onEditPress={openManualEntry}
                        />
                        <View
                            marginHorizontal={
                                CHAT_INPUT_WIDGET.rowPaddingHorizontal
                            }
                        >
                            <Button
                                text="Next"
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                width="full"
                                disabled={
                                    isWidgetDisabled ||
                                    (balances[currentIndex] ?? 0) === 0
                                }
                                onPress={handleNext}
                            />
                        </View>
                    </YStack>
                )}
            </YStack>
        </BaseInputWidget>
    );
};
